import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Key } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const KINDS = ["aws_kms", "azure_kv", "gcp_kms"];
const empty = { org_id: "", kind: "aws_kms", key_ref: "", creds_provider: "" };

export default function KMSKeys() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/kms-keys"); setItems(data); }
    catch (err) { console.error("kms load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/kms-keys", form); toast.success("KMS key registered"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this KMS key registration?")) return;
    try { await api.delete(`/kms-keys/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="KMS Keys" subtitle="ENVELOPE ENCRYPTION">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="kms-count">
          {items.length} KEYS · {KINDS.map((k) => `${items.filter((i) => i.kind === k).length} ${k.toUpperCase()}`).join(" · ")}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-kms-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> REGISTER KEY</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Key className="w-4 h-4 text-cyan-400" /> Register KMS Key</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div><L>Org id</L><Input data-testid="kms-org-input" value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Provider</L>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="kms-kind-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {KINDS.map((k) => <SelectItem key={k} value={k} className="font-mono uppercase text-xs">{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><L>Key ref</L><Input data-testid="kms-keyref-input" placeholder="arn:aws:kms:us-east-1:111111111111:key/abc-def" value={form.key_ref} onChange={(e) => setForm({ ...form, key_ref: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Credentials provider id</L><Input data-testid="kms-creds-input" placeholder="cloud_credentials._id" value={form.creds_provider} onChange={(e) => setForm({ ...form, creds_provider: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-kms-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">REGISTER</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="kms-list">
        {items.map((k) => (
          <div key={k.kms_id} data-testid={`kms-card-${k.kms_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight uppercase">{k.kind}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{k.kms_id}</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-cyan-500/40 text-cyan-400 bg-cyan-500/10 rounded-sm">org {k.org_id}</span>
            </div>
            <div className="font-mono text-[11px] text-zinc-400 break-all">{k.key_ref}</div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">creds: {k.creds_provider}</span>
              {user?.role === "admin" && (
                <Button size="sm" variant="ghost" data-testid={`delete-kms-${k.kms_id}`} onClick={() => del(k.kms_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no KMS keys registered.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
